'use client'

import { useState, useEffect } from 'react'
import * as z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { nanoid } from 'nanoid'
import { useTranslations } from 'next-intl'
import { Monitor, Smartphone } from 'lucide-react'

import type { UserProject } from '@/lib/types'
import { uploadBlobToCdn } from '@/lib/utils'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { popModal } from '@/components/modals'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'

interface Props {
  project: UserProject
  onSuccess?: () => void
  onClose?: () => void
}

const CATEGORIES = [
  { id: 'app', label: 'App' },
  { id: 'game', label: 'Game' },
  { id: 'website', label: 'Website' },
]

const formSchema = z.object({
  title: z.string().min(2, {
    message: 'Title must be at least 2 characters.',
  }),
  category: z.string().min(1, {
    message: 'Please select a category.',
  }),
  device_view: z.enum(['desktop', 'mobile']),
  image: z
    .any()
    .optional()
    .refine(
      (files) => !files || files.length === 0 || files.length === 1,
      'Please select only one image.',
    )
    .refine(
      (files) =>
        !files || files.length === 0 || files[0]?.type?.startsWith('image/'),
      'Please select an image file.',
    ),
})

type FormData = z.infer<typeof formSchema>

export default function ProjectToTemplateModal({
  project,
  onSuccess,
  onClose = popModal,
}: Props) {
  const [loading, setLoading] = useState(false)
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(
    project?.thumbnail || null,
  )
  const t = useTranslations('modals.project-to-template')

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: project.project_name,
      category: '',
      device_view: 'desktop',
    },
  })

  const imageFile = form.watch('image')?.[0]
  // Determine if we have an image to display (either from project or newly uploaded)
  const hasImage = Boolean(existingImageUrl) || Boolean(imageFile)

  async function onSubmit(values: FormData) {
    try {
      setLoading(true)

      let imageUrl = existingImageUrl

      // Only upload a new image if one was selected
      if (values.image?.[0]) {
        imageUrl = await uploadBlobToCdn(
          values.image[0],
          `templates/${nanoid()}.${values.image[0].name.split('.').pop()}`,
          values.image[0].type,
        )
      } else if (!imageUrl && project.thumbnail) {
        // Use project thumbnail as fallback if no image was selected and no existing image
        imageUrl = project.thumbnail
      }

      if (!imageUrl) {
        toast.error('An image is required')
        setLoading(false)
        return
      }

      const endpoint = `/api/dashboard/projects/${project._id}/template`

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-request-id': nanoid(12),
        },
        body: JSON.stringify({
          title: values.title,
          category: values.category,
          image_url: imageUrl,
          device_view: values.device_view,
        }),
      })

      if (!res.ok) throw new Error('Failed to create template')

      toast.success(t('success'))
      onSuccess?.()
      onClose()
    } catch (error) {
      console.error(error)
      toast.error(t('error'))
    } finally {
      setLoading(false)
    }
  }

  // Set project thumbnail as default image
  useEffect(() => {
    if (project?.thumbnail) {
      setExistingImageUrl(project.thumbnail)
    }
  }, [project])

  // Function to clear the image
  const clearImage = () => {
    form.setValue('image', null)
    setExistingImageUrl(null)
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{t('title')}</DialogTitle>
        <DialogDescription>
          {t('description', { projectName: project.project_name })}
        </DialogDescription>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('form.title')}</FormLabel>
                <FormControl>
                  <Input placeholder={t('form.title_placeholder')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="category"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('form.category')}</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t('form.category_placeholder')}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="device_view"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>Default Device View</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex space-x-4"
                  >
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <RadioGroupItem value="desktop" id="create-desktop" />
                      </FormControl>
                      <FormLabel
                        htmlFor="create-desktop"
                        className="flex items-center gap-1 cursor-pointer"
                      >
                        <Monitor className="h-4 w-4" /> Desktop
                      </FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <RadioGroupItem value="mobile" id="create-mobile" />
                      </FormControl>
                      <FormLabel
                        htmlFor="create-mobile"
                        className="flex items-center gap-1 cursor-pointer"
                      >
                        <Smartphone className="h-4 w-4" /> Mobile
                      </FormLabel>
                    </FormItem>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="image"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('form.screenshot')}</FormLabel>

                {hasImage && (
                  <div className="mb-4">
                    {imageFile ? (
                      <div className="flex items-center justify-between p-2 border rounded-md">
                        <p className="text-sm text-gray-600">
                          {imageFile.name}
                        </p>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={clearImage}
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      existingImageUrl && (
                        <div>
                          <img
                            src={existingImageUrl}
                            alt="Project thumbnail"
                            className="rounded-lg max-h-[100px] object-cover mb-2"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={clearImage}
                          >
                            Remove
                          </Button>
                        </div>
                      )
                    )}
                  </div>
                )}

                {!hasImage && (
                  <FormControl>
                    <Input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={(e) => field.onChange(e.target.files)}
                    />
                  </FormControl>
                )}

                <FormMessage />
              </FormItem>
            )}
          />
          <DialogFooter>
            <Button type="submit" disabled={loading}>
              {loading ? t('form.creating') : t('form.create')}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  )
}
